import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchEventsCatalog, } from '@/entities/agent';
import { ApiError } from '@/shared/api';
import { uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Button, HelpTip, IconButton, MetaStrip, Skeleton, StageTabs, } from '@/shared/ui';
import { EventsFilters } from './events-filters';
import { EventsTable } from './events-table';
const PAGE_SIZE = 50;
const FORMAT_TABS = [
    { id: 'all', label: 'Все' },
    { id: 'bt', label: 'Брокер-туры' },
    { id: 'other', label: 'Другие мероприятия' },
];
function parseFormat(value) {
    if (value === 'bt' || value === 'other')
        return value;
    return 'all';
}
export function EventsPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const format = parseFormat(searchParams.get('format'));
    const filters = useMemo(() => ({
        q: searchParams.get('q') ?? '',
        project: searchParams.get('project') ?? 'all',
        period: searchParams.get('period') ?? 'all',
    }), [searchParams]);
    const offset = Math.max(Number(searchParams.get('offset') ?? '0') || 0, 0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await fetchEventsCatalog({
                q: filters.q || undefined,
                format: format === 'all' ? undefined : format,
                project: filters.project === 'all' ? undefined : filters.project,
                period: filters.period === 'all' ? undefined : filters.period,
                limit: PAGE_SIZE,
                offset,
            }));
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : uiCopy.actionFailed);
            setData(null);
        }
        finally {
            setLoading(false);
        }
    }, [filters.q, filters.project, filters.period, format, offset]);
    useEffect(() => {
        void load();
    }, [load]);
    function writeParams(next, resetOffset = true) {
        const params = new URLSearchParams(searchParams);
        const apply = (key, value, empty = 'all') => {
            if (!value || value === empty)
                params.delete(key);
            else
                params.set(key, value);
        };
        if ('format' in next)
            apply('format', next.format);
        if ('q' in next) {
            if (!next.q)
                params.delete('q');
            else
                params.set('q', next.q);
        }
        if ('project' in next)
            apply('project', next.project);
        if ('period' in next)
            apply('period', next.period);
        if (resetOffset)
            params.delete('offset');
        else if ('offset' in next) {
            if (next.offset == null || next.offset <= 0)
                params.delete('offset');
            else
                params.set('offset', String(next.offset));
        }
        setSearchParams(params, { replace: true });
    }
    function applyFilters(next) {
        writeParams({
            q: next.q,
            project: next.project,
            period: next.period,
        });
    }
    function resetFilters() {
        writeParams({ q: '', project: 'all', period: 'all' });
    }
    function applyFormat(next) {
        writeParams({ format: next });
    }
    function openEvent(id) {
        navigate(`/events/${encodeURIComponent(id)}`);
    }
    const total = data?.total ?? 0;
    const shown = data?.items.length ?? 0;
    const inviteSum = data?.items.reduce((sum, row) => sum + row.inviteCount, 0) ?? 0;
    const visitorSum = data?.items.reduce((sum, row) => sum + row.visitorCount, 0) ?? 0;
    const canPrev = offset > 0;
    const canNext = offset + shown < total;
    return (_jsxs("div", { className: "space-y-3 px-0 py-1", children: [_jsxs("header", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsx("div", { className: "space-y-1", children: _jsx("h1", { className: "text-xl font-semibold", children: "\u041C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u044F" }) }), _jsx(HelpTip, { open: helpOpen, onOpenChange: setHelpOpen, align: "end", title: "\u041A\u0430\u0442\u0430\u043B\u043E\u0433 \u043C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u0439", trigger: _jsx(IconButton, { size: "sm", pressed: helpOpen, "aria-label": "\u0421\u043F\u0440\u0430\u0432\u043A\u0430 \u043F\u043E \u043C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u044F\u043C", "aria-expanded": helpOpen, children: _jsx(Info, { "aria-hidden": true }) }), children: _jsx("p", { children: "\u041E\u0431\u0449\u0438\u0439 \u043A\u0430\u0442\u0430\u043B\u043E\u0433 CRM. \u0423\u0447\u0430\u0441\u0442\u0438\u0435 \u0430\u0433\u0435\u043D\u0442\u0430 \u043D\u0435 \u0441\u043E\u0437\u0434\u0430\u0451\u0442 \u043E\u0442\u0434\u0435\u043B\u044C\u043D\u043E\u0435 \u0432\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u0435. \u041F\u043E\u0441\u0435\u0449\u0435\u043D\u0438\u0435 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0430\u0435\u0442 \u043E\u0442\u0437\u044B\u0432; \u043F\u0440\u0438\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435 \u0441\u0430\u043C\u043E \u043F\u043E \u0441\u0435\u0431\u0435 \u043F\u043E\u0441\u0435\u0449\u0435\u043D\u0438\u0435 \u043D\u0435 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0430\u0435\u0442." }) })] }), _jsx(StageTabs, { items: [...FORMAT_TABS], value: format, onValueChange: applyFormat, "aria-label": "\u0424\u043E\u0440\u043C\u0430\u0442 \u043C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u0439" }), _jsx(EventsFilters, { filters: filters, total: total, projects: data?.projects ?? [], onChange: applyFilters, onReset: resetFilters }), _jsx(MetaStrip, { primary: `${shown} на странице`, items: [
                    _jsxs(_Fragment, { children: ["\u0438\u0437 ", total] }),
                    _jsxs(_Fragment, { children: ["\u041F\u0440\u0438\u0433\u043B\u0430\u0448\u0435\u043D\u043E: ", inviteSum] }),
                    _jsxs(_Fragment, { children: ["\u041F\u043E\u0441\u0435\u0442\u0438\u043B\u0438: ", visitorSum] }),
                    ...(offset > 0 ? [_jsxs(_Fragment, { children: ["\u0441 \u043F\u043E\u0437\u0438\u0446\u0438\u0438 ", offset + 1] })] : []),
                ] }), error ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error }), _jsx(Button, { type: "button", variant: "outline", className: "mt-3", onClick: () => void load(), children: uiCopy.retry })] })) : null, loading ? (_jsx(Skeleton, { className: "h-64 w-full" })) : data ? (_jsxs(_Fragment, { children: [_jsx(EventsTable, { rows: data.items, onOpenEvent: openEvent }), total > PAGE_SIZE ? (_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Button, { type: "button", size: "sm", variant: "outline", disabled: !canPrev, onClick: () => writeParams({ offset: Math.max(0, offset - PAGE_SIZE) }, false), children: "\u041D\u0430\u0437\u0430\u0434" }), _jsx(Button, { type: "button", size: "sm", variant: "outline", disabled: !canNext, onClick: () => writeParams({ offset: offset + PAGE_SIZE }, false), children: "\u0414\u0430\u043B\u0435\u0435" })] })) : null] })) : null] }));
}
